import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { StorageService } from '../services/storage.service';
import { UserSettingService } from '../services/user-setting.service';
import { AppShellService } from '../services/app-shell.service';
import { Role, UserDetail } from '../models/accounts.model';
import { jwtDecode } from 'jwt-decode';

// Deep links opened from the mobile app only carry the raw JWT via query params (no login
// response ever runs), so UserSettingService's stored profile is never populated and
// role-gated UI (e.g. the Admin-only "set posts limit" control) silently fails to show.
// Decoding the token's claims here keeps that profile in sync for deep-link sessions too.
function decodeJwtClaims(token: string): Record<string, any> | null {
  try {
    return jwtDecode<Record<string, any>>(token.replace(/^Bearer\s+/i, ''));
  } catch {
    return null;
  }
}

// The API may emit claims as short names ("role") or as .NET claim-type URIs ending in "/role",
// so match on the last segment of the key.
function getClaim(claims: Record<string, any>, name: string): any {
  const key = Object.keys(claims).find(k => k.split('/').pop()?.toLowerCase() === name);
  return key ? claims[key] : undefined;
}

export const authGuard: CanActivateFn = (route, state) => {
  const storageService = inject(StorageService);
  const userSettingService = inject(UserSettingService);
  const router = inject(Router);
  const appShell = inject(AppShellService);

  // Capture tokens from query parameters if accessed via mobile webview
  const token = route.queryParams['token'] || route.queryParams['userToken'];
  const refreshToken = route.queryParams['refreshToken'];

  if (token) {
    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    storageService.add('userToken', bearerToken);

    const claims = decodeJwtClaims(bearerToken);
    if (claims) {
      const existing = userSettingService.getData();
      const userRole = (getClaim(claims, 'role') as Role) || existing.userRole;
      const masjidId = claims['MasjidId'] ? Number(claims['MasjidId']) : existing.masjidId;
      const organizationId = claims['OrganizationId'] ? Number(claims['OrganizationId']) : existing.organizationId;

      userSettingService.sendData({
        ...existing,
        accessToken: bearerToken,
        refreshToken: refreshToken || existing.refreshToken,
        userRole,
        userName: getClaim(claims, 'name') || existing.userName,
        masjidId,
        organizationId
      } as UserDetail);
    }
  }
  if (refreshToken) {
    storageService.add('refreshToken', refreshToken);
  }

  // The tokens are persisted now, so drop them from the URL: otherwise they stay in the webview
  // history and get carried to every screen by queryParamsHandling: 'merge'.
  if (token || refreshToken) {
    const cleanUrl = router.parseUrl(state.url);
    const { token: _token, userToken: _userToken, refreshToken: _refreshToken, ...queryParams } = cleanUrl.queryParams;
    cleanUrl.queryParams = queryParams;
    return new RedirectCommand(cleanUrl, { replaceUrl: true });
  }

  const userToken = storageService.get('userToken');

  if (userToken) {
    return true;
  } else {
    // Embedded (app webview) routes inherit `embedded` from their parent route's data.
    appShell.redirectToLogin(state.url, !!route.data['embedded']);
    return false;
  }
};
